import { useMutatePlan } from "../../../hooks/useMutatePlan";
import { EditMode, MembershipType, Plan } from "../../../models";
import { EmployerModuleType } from "../../../models/accounts/employerModuleType";
import { isLoading } from "../../../utils/isLoading";
import { useData } from "../../../utils/useData";
import { CreatePlanInput } from "../../plans/CreatePlanInput";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { PlanFormBase, PlanFormOutput } from "../PlanFormBase";
import { RewardSettingsFields } from "./RewardSettingsFields";
import { useConfig } from "../../../utils/useConfig";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { toast } from "react-toastify";
import { RewardReasonFields } from "./RewardReasonFields";
import { RewardsTiersFields } from "./RewardsTiersFields";
import { AccountKind } from "../../../models/accounts/accountKind";
import { useMutation as useApolloMutation } from "@apollo/client";
import { CREATE_REWARD_SENDING_PLAN, CreatePlanRequestInput } from "../../../graphql/operations";
import { ServiceSectorReference } from "../../../models/serviceSectors/serviceSectorReference";
import { StandardRenewalOptionsFields } from "../Standard/StandardRenewalOptionsFields";

export const RewardsForm = ({ reward, onClose }: { reward?: Plan, onClose: () => void }) => {
    const { serviceSectors, accounts, membership } = useData();
    const { config } = useConfig();
    const { employerId, plans } = useData();
    const { getToken } = useKindeAuth();

    const editMode = reward == null ? EditMode.CREATE : EditMode.EDIT;
    const isPilot = membership.data?.type === MembershipType.PILOT;

    const mutateReward = useMutatePlan({
        onSuccess: onClose,
        onError: (error) => console.log(error.message),
        successMessage: "Reward updated successfully"
    });

    const [createRewardSendingPlan, { loading: createRewardLoading }] = useApolloMutation<boolean, CreatePlanRequestInput>(
      CREATE_REWARD_SENDING_PLAN,
      {
        onCompleted: () => {
          toast.success("Reward updated successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
        }
      }
    );

    const handleRewardSave = async (
        values: PlanFormOutput
    ) => {
      console.log(values)
        if(!reward) {
            if (!values.tiers.length) {
              throw new Error("Reward must have at least 1 tier");
            }

            const serviceSectorRefs = values.serviceSectorIds.map(id => {
              const sector = serviceSectors.data?.items.find(s => s.id === id);
              return {
                id,
                name: sector?.name || ""
              } as ServiceSectorReference;
            });

            await createRewardSendingPlan({
              variables: {
                employerId: employerId,
                request: {
                  accountKind: AccountKind.REWARDS_SENDING,
                  name: values.name,
                  serviceSectorIds: values.serviceSectorIds,
                  tiers: values.tiers.map(tier => ({
                    name: tier.name,
                    amount: tier.amount.amount
                  })),
                  type: values.type,
                  employerModuleType: EmployerModuleType.REWARDS_RECOGNITION,
                  renewalOptions: values.renewalOptions,
                  controls: {
                    payments: {
                      isEnabled: true,
                      reasonCategories: values.controls?.payments?.reasonCategories
                    },
                    purchases: {
                      isEnabled: false
                    },
                    management: {
                      canAdHocTopUp: true
                    },
                    serviceSectors: serviceSectorRefs
                  }
                }
              }
            });
        } else {
            await mutateReward.mutate({
                ...reward,
                name: values.name,
                tiers: values.tiers, 
                controls: {
                    ...reward.controls,
                    payments: {
                        isEnabled: true,
                        reasonCategories: values.controls?.payments?.reasonCategories
                    }
                }
            });
        }
    }

    if(isLoading([serviceSectors])) return <LoadingSpinner />

    return (
        <PlanFormBase
            existingPlan={reward}
            moduleType={EmployerModuleType.REWARDS_RECOGNITION}
            isLoading={createRewardLoading}
            serviceSectors={serviceSectors.data?.items ?? []}
            onClose={onClose}
            onSave={handleRewardSave}
        >
            <RewardSettingsFields />
            <StandardRenewalOptionsFields isPilot={isPilot} editMode={editMode} />
            <RewardReasonFields />
            <RewardsTiersFields isPilot={isPilot} accounts={accounts.data!.items} editMode={editMode} />
        </PlanFormBase>
    )
}